import React from "react";
import PropTypes from "prop-types";
import { AnalysisSkeleton } from "./AnalysisSkeleton";

import { useThemeA } from "../../internal/ThemeAContext";

const VARIANT_CLASSES = Object.freeze({
  avatar: "akv:h-11 akv:w-11 akv:rounded-full",
  block: "akv:h-28 akv:w-full akv:rounded-ak-surface",
  button: "akv:h-10 akv:w-28 akv:rounded-ak-control",
  custom: "",
  text: "akv:h-3 akv:w-full akv:rounded-full",
  title: "akv:h-6 akv:w-2/3 akv:max-w-80 akv:rounded-full",
});

export function Skeleton({ className = "", variant = "block" }) {
  useThemeA("Skeleton");

  return (
    <span
      aria-hidden="true"
      className={`${VARIANT_CLASSES[variant]} akv:relative akv:block akv:overflow-hidden akv:bg-ak-subtle akv:before:absolute akv:before:inset-0 akv:before:-translate-x-full akv:before:bg-[linear-gradient(90deg,transparent,var(--ak-v2-skeleton-shimmer),transparent)] akv:before:animate-[ak-skeleton-shimmer_1.45s_ease-in-out_infinite] akv:motion-reduce:before:animate-none ${className}`}
      data-ak-v2-component="skeleton"
    />
  );
}

Skeleton.propTypes = {
  className: PropTypes.string,
  variant: PropTypes.oneOf(Object.keys(VARIANT_CLASSES)),
};

const CardSkeleton = () => (
  <div className="akv:grid akv:min-h-40 akv:gap-5 akv:rounded-ak-surface akv:bg-ak-surface akv:p-5 akv:shadow-ak-surface">
    <div className="akv:flex akv:items-center akv:gap-3">
      <Skeleton variant="avatar" />
      <div className="akv:grid akv:min-w-0 akv:flex-1 akv:gap-2">
        <Skeleton className="akv:w-3/5" variant="text" />
        <Skeleton className="akv:w-2/5" variant="text" />
      </div>
    </div>
    <div className="akv:grid akv:gap-2">
      <Skeleton variant="text" />
      <Skeleton className="akv:w-4/5" variant="text" />
    </div>
  </div>
);

const HeaderSkeleton = () => (
  <div className="akv:flex akv:items-end akv:justify-between akv:gap-5" data-skeleton-zone="header">
    <div className="akv:grid akv:min-w-0 akv:flex-1 akv:gap-3">
      <Skeleton variant="title" />
      <Skeleton className="akv:w-2/5" variant="text" />
    </div>
    <Skeleton variant="button" />
  </div>
);

const MetricsSkeleton = ({ count = 4 }) => (
  <div className="akv:grid akv:grid-cols-4 akv:gap-4 akv:max-[72rem]:grid-cols-2 akv:max-[42rem]:grid-cols-1" data-skeleton-zone="metrics">
    {Array.from({ length: count }, (_, index) => (
      <div className="akv:flex akv:items-center akv:gap-3 akv:rounded-ak-surface akv:bg-ak-surface akv:p-4 akv:shadow-ak-surface" key={index}>
        <Skeleton variant="avatar" />
        <div className="akv:grid akv:flex-1 akv:gap-2"><Skeleton className="akv:w-2/5" variant="title" /><Skeleton className="akv:w-3/5" variant="text" /></div>
      </div>
    ))}
  </div>
);

const TabsSkeleton = ({ count = 5 }) => (
  <div className="akv:flex akv:flex-wrap akv:gap-2" data-skeleton-zone="tabs">
    {Array.from({ length: count }, (_, index) => <Skeleton className={`akv:h-10 ${index === 0 ? "akv:w-28" : "akv:w-24"}`} key={index} variant="button" />)}
  </div>
);

/**
 * Exact loading geometry for the naturalist identity plate used by animal
 * records. Keeping it next to the shared Skeleton primitive makes the route
 * loader and the progressively mounted production editor use one specimen.
 */
export function ReferencePhotoIdentitySkeleton() {
  useThemeA("ReferencePhotoIdentitySkeleton");

  return (
    <div
      aria-hidden="true"
      className="akv:min-w-0 akv:overflow-hidden akv:rounded-ak-surface akv:bg-ak-surface akv:shadow-ak-surface"
      data-ak-v2-component="reference-photo-identity-skeleton"
    >
      <div className="akv:grid akv:min-w-0 akv:grid-cols-[6rem_minmax(0,1fr)_auto] akv:max-[54rem]:grid-cols-[6rem_minmax(0,1fr)] akv:items-center akv:gap-5 akv:px-6 akv:py-5 akv:max-[34rem]:grid-cols-1 akv:max-[40rem]:p-4">
        <div className="akv:min-w-0" data-skeleton-zone="reference-photo">
          <Skeleton className="akv:aspect-square akv:h-24 akv:w-24 akv:rounded-ak-surface" variant="custom" />
        </div>

        <div className="akv:flex akv:min-w-0 akv:flex-col akv:justify-center" data-skeleton-zone="reference-identity">
          <div className="akv:mb-2 akv:flex akv:flex-wrap akv:gap-2">
            <Skeleton className="akv:h-7 akv:w-20 akv:rounded-full" variant="custom" />
            <Skeleton className="akv:h-7 akv:w-36 akv:rounded-ak-control" variant="custom" />
          </div>
          <Skeleton className="akv:h-3 akv:w-32" variant="custom" />
          <Skeleton className="akv:mt-3 akv:h-8 akv:w-56" variant="custom" />
          <Skeleton className="akv:mt-3 akv:w-36" variant="text" />
          <Skeleton className="akv:mt-2 akv:w-28" variant="text" />
          <div className="akv:mt-2 akv:flex akv:flex-wrap akv:gap-2" data-skeleton-zone="taxonomy">
            <Skeleton className="akv:h-8 akv:w-24 akv:rounded-ak-control" variant="custom" />
            <Skeleton className="akv:h-8 akv:w-28 akv:rounded-ak-control" variant="custom" />
            <Skeleton className="akv:h-8 akv:w-24 akv:rounded-ak-control" variant="custom" />
          </div>
        </div>
          <div className="akv:flex akv:flex-wrap akv:gap-2 akv:max-[54rem]:col-span-2 akv:max-[34rem]:col-span-1" data-skeleton-zone="photo-actions">
            {Array.from({ length: 3 }, (_, index) => (
              <Skeleton className="akv:h-9 akv:w-9 akv:rounded-ak-control" key={index} variant="custom" />
            ))}
          </div>
      </div>
    </div>
  );
}

// Récapitulatif « Décision & suivi » en tête des fiches : titre et pastille, ligne de faits,
// deux raccourcis à droite (FollowUpSummaryCard).
const FollowUpSummarySkeleton = () => (
  <div className="akv:flex akv:flex-wrap akv:items-center akv:justify-between akv:gap-4 akv:rounded-ak-surface akv:bg-ak-surface akv:p-6 akv:shadow-ak-raised akv:max-[48rem]:p-4" data-skeleton-zone="follow-up-summary">
    <div className="akv:grid akv:min-w-0 akv:flex-[1_1_16rem] akv:gap-2">
      <div className="akv:flex akv:items-center akv:gap-3">
        <Skeleton className="akv:h-5 akv:w-40" variant="custom" />
        <Skeleton className="akv:h-6 akv:w-28 akv:rounded-full" variant="custom" />
      </div>
      <Skeleton className="akv:w-72 akv:max-w-full" variant="text" />
    </div>
    <div className="akv:flex akv:gap-2">
      <Skeleton className="akv:h-10 akv:w-36 akv:rounded-ak-control" variant="custom" />
      <Skeleton className="akv:h-10 akv:w-40 akv:rounded-ak-control" variant="custom" />
    </div>
  </div>
);

// Contenu de l'onglet d'identité de la fiche individu (vue entraînée) pendant son hydratation.
const IndividualContentSkeleton = () => (
  <div className="akv:grid akv:gap-5 akv:rounded-ak-surface akv:bg-ak-surface akv:p-6 akv:shadow-ak-surface akv:max-[40rem]:p-4" data-skeleton-zone="identity-fields">
    <div className="akv:flex akv:items-center akv:gap-3">
      <Skeleton className="akv:h-10 akv:w-10 akv:rounded-ak-control" variant="custom" />
      <Skeleton className="akv:h-6 akv:w-52" variant="custom" />
    </div>
    <div className="akv:grid akv:grid-cols-4 akv:gap-4 akv:max-[70rem]:grid-cols-2 akv:max-[40rem]:grid-cols-1">
      {Array.from({ length: 8 }, (_, index) => (
        <div className="akv:grid akv:gap-2" key={index}>
          <Skeleton className="akv:h-3 akv:w-24" variant="custom" />
          <Skeleton className="akv:h-10" variant="block" />
        </div>
      ))}
    </div>
    <Skeleton className="akv:h-16" variant="block" />
  </div>
);

// Bandeau d'identité d'une fiche (EntityIdentityBanner) : média, surtitre, titre, description
// et faits. Les fiches prêtes montent le vrai bandeau en `loading` ; ce dessin sert au premier
// chargement de la route, avant que la fiche soit montée.
const IdentityBannerSkeleton = ({ facts = 0 }) => (
  <div className="akv:grid akv:min-w-0 akv:grid-cols-[auto_minmax(12rem,1fr)_auto] akv:items-center akv:gap-5 akv:rounded-ak-surface akv:bg-ak-surface akv:px-6 akv:py-5 akv:shadow-ak-surface akv:max-[54rem]:grid-cols-[auto_minmax(0,1fr)]" data-skeleton-zone="identity-banner">
    <div className="akv:rounded-ak-surface akv:bg-ak-brand-soft akv:p-1.5"><Skeleton className="akv:h-20 akv:w-20 akv:rounded-ak-control" variant="custom" /></div>
    <div className="akv:grid akv:min-w-0 akv:gap-2">
      <Skeleton className="akv:h-2.5 akv:w-28" variant="custom" />
      <Skeleton className="akv:h-6 akv:w-48" variant="custom" />
      <Skeleton className="akv:h-3 akv:w-40" variant="custom" />
    </div>
    {facts ? (
      <div className="akv:grid akv:grid-flow-col akv:auto-cols-[minmax(8rem,auto)] akv:gap-6 akv:max-[54rem]:col-span-2">
        {Array.from({ length: facts }, (_, index) => (
          <div className="akv:grid akv:gap-2" key={index}><Skeleton className="akv:h-2.5 akv:w-16" variant="custom" /><Skeleton className="akv:h-4 akv:w-24" variant="custom" /></div>
        ))}
      </div>
    ) : null}
  </div>
);

// Bande de cinq indicateurs dans une surface (MetricStrip des tableaux de bord enclos et secteur).
const MetricStripSkeleton = () => (
  <div className="akv:grid akv:grid-cols-5 akv:gap-4 akv:rounded-ak-surface akv:bg-ak-surface akv:p-5 akv:shadow-ak-surface akv:max-[64rem]:grid-cols-3 akv:max-[40rem]:grid-cols-2" data-skeleton-zone="metric-strip">
    {Array.from({ length: 5 }, (_, index) => (
      <div className="akv:grid akv:gap-2" key={index}><Skeleton className="akv:h-7 akv:w-12" variant="custom" /><Skeleton className="akv:h-3 akv:w-24" variant="custom" /></div>
    ))}
  </div>
);

// Tableau de bord d'enclos ou de secteur (vue entraînée) : indicateurs, activité et informations.
const DashboardContentSkeleton = () => (
  <div className="akv:grid akv:gap-5" data-skeleton-zone="dashboard">
    <MetricStripSkeleton />
    <div className="akv:grid akv:grid-cols-[minmax(0,2fr)_minmax(16rem,1fr)] akv:gap-5 akv:max-[64rem]:grid-cols-1">
      <Skeleton className="akv:h-72" variant="block" />
      <div className="akv:grid akv:content-start akv:gap-3 akv:rounded-ak-surface akv:bg-ak-surface akv:p-5 akv:shadow-ak-surface">
        <Skeleton variant="title" />
        {Array.from({ length: 4 }, (_, index) => <Skeleton className="akv:h-9" key={index} variant="block" />)}
      </div>
    </div>
  </div>
);

// Onglet Informations d'un groupe (vue entraînée) : formulaire en carte.
const GroupInfoContentSkeleton = () => (
  <div className="akv:grid akv:gap-5 akv:rounded-ak-surface akv:bg-ak-surface akv:p-6 akv:shadow-ak-surface akv:max-[40rem]:p-4" data-skeleton-zone="group-info">
    <Skeleton className="akv:h-6 akv:w-44" variant="custom" />
    <div className="akv:grid akv:grid-cols-2 akv:gap-4 akv:max-[48rem]:grid-cols-1">
      {Array.from({ length: 6 }, (_, index) => (
        <div className="akv:grid akv:gap-2" key={index}><Skeleton className="akv:h-3 akv:w-28" variant="custom" /><Skeleton className="akv:h-10" variant="block" /></div>
      ))}
    </div>
    <Skeleton className="akv:h-24" variant="block" />
  </div>
);

// Fiche (groupe, enclos, secteur) : bandeau, récapitulatif du suivi, onglets seulement sous
// 941 px (au-delà, le panneau de gauche les porte), puis le contenu de la rubrique par défaut.
const RecordSkeleton = ({ variant, facts = 0, metrics = false, children }) => (
  <div className="akv:grid akv:gap-6" data-ak-v2-component="skeleton-layout" data-variant={variant}>
    <IdentityBannerSkeleton facts={facts} />
    <FollowUpSummarySkeleton />
    <div className="akv:min-[941px]:hidden"><TabsSkeleton count={6} /></div>
    {metrics ? <MetricStripSkeleton /> : null}
    {children}
  </div>
);

const RegistrySkeleton = ({ cards = false }) => (
  <div className="akv:grid akv:gap-4" data-skeleton-zone="registry">
    <div className="akv:grid akv:grid-cols-[2fr_1fr_auto] akv:gap-3 akv:rounded-ak-surface akv:bg-ak-surface akv:p-4 akv:shadow-ak-surface akv:max-[48rem]:grid-cols-1">
      <Skeleton className="akv:h-10" variant="block" /><Skeleton className="akv:h-10" variant="block" /><Skeleton variant="button" />
    </div>
    <div className={cards ? "akv:grid akv:grid-cols-3 akv:gap-4 akv:max-[72rem]:grid-cols-2 akv:max-[42rem]:grid-cols-1" : "akv:grid akv:gap-2"}>
      {Array.from({ length: cards ? 3 : 5 }, (_, index) => cards ? <CardSkeleton key={index} /> : (
        <div className="akv:flex akv:items-center akv:gap-4 akv:rounded-ak-control akv:bg-ak-surface akv:px-4 akv:py-3" key={index}>
          <Skeleton variant="avatar" /><Skeleton className="akv:flex-1" variant="text" /><Skeleton className="akv:w-24" variant="text" />
        </div>
      ))}
    </div>
  </div>
);

const FeatureSkeleton = ({ variant }) => {
  if (variant === "analysis") return (
    <div className="akv:grid akv:gap-6" data-ak-v2-component="skeleton-layout" data-variant={variant}>
      <HeaderSkeleton /><MetricsSkeleton /><TabsSkeleton count={6} />
      <div className="akv:grid akv:grid-cols-[minmax(0,2fr)_minmax(18rem,1fr)] akv:gap-5 akv:max-[64rem]:grid-cols-1" data-skeleton-zone="analysis-workspace">
        <Skeleton className="akv:h-[28rem]" variant="block" />
        <div className="akv:grid akv:gap-3 akv:rounded-ak-surface akv:bg-ak-surface akv:p-5 akv:shadow-ak-surface"><Skeleton variant="title" />{Array.from({ length: 6 }, (_, index) => <Skeleton className="akv:h-10" key={index} variant="block" />)}</div>
      </div>
    </div>
  );
  if (variant === "veterinary") return (
    <div className="akv:grid akv:gap-6" data-ak-v2-component="skeleton-layout" data-variant={variant}>
      <HeaderSkeleton /><TabsSkeleton count={3} /><TabsSkeleton count={4} />
      <div className="akv:grid akv:grid-cols-[auto_auto_auto_1fr] akv:gap-3 akv:rounded-ak-surface akv:bg-ak-surface akv:p-4 akv:shadow-ak-surface akv:max-[64rem]:grid-cols-2"><Skeleton variant="button" /><Skeleton variant="button" /><Skeleton variant="button" /><Skeleton className="akv:h-10" variant="block" /></div>
      <RegistrySkeleton cards />
    </div>
  );
  // Fiches groupe, enclos et secteur (retour du 13/10, mêmes règles que la fiche individu).
  if (variant === "sector") return <RecordSkeleton metrics variant={variant}><DashboardContentSkeleton /></RecordSkeleton>;
  if (variant === "enclosure") return <RecordSkeleton variant={variant}><DashboardContentSkeleton /></RecordSkeleton>;
  if (variant === "group") return <RecordSkeleton facts={3} variant={variant}><GroupInfoContentSkeleton /></RecordSkeleton>;
  if (variant === "sector-dashboard" || variant === "enclosure-dashboard") return (
    <div data-ak-v2-component="skeleton-layout" data-variant={variant}><DashboardContentSkeleton /></div>
  );
  if (variant === "group-info") return (
    <div data-ak-v2-component="skeleton-layout" data-variant={variant}><GroupInfoContentSkeleton /></div>
  );
  if (variant === "tour") return (
    <div className="akv:grid akv:gap-6" data-ak-v2-component="skeleton-layout" data-variant={variant}><HeaderSkeleton /><TabsSkeleton count={5} /><div className="akv:grid akv:grid-cols-[minmax(0,1fr)_minmax(18rem,0.7fr)] akv:gap-4 akv:max-[64rem]:grid-cols-1"><Skeleton className="akv:h-72" variant="block" /><div className="akv:grid akv:gap-3"><CardSkeleton /><Skeleton className="akv:h-24" variant="block" /></div></div></div>
  );

  // Fiche individu (retour du 13/10, clignotement au chargement) : même géométrie que la page
  // prête. Les actions vivent dans l'en-tête (`record-actions`), pas sur une rangée à part ; la
  // plaque d'identité, le récapitulatif du suivi puis le contenu de l'onglet se succèdent ; la
  // rangée d'onglets n'existe que sous 941 px (au-delà, le panneau de gauche la remplace).
  if (variant === "individual") return (
    <div className="akv:grid akv:gap-6" data-ak-v2-component="skeleton-layout" data-variant={variant}>
      <ReferencePhotoIdentitySkeleton />
      <FollowUpSummarySkeleton />
      <div className="akv:min-[941px]:hidden"><TabsSkeleton count={6} /></div>
      <IndividualContentSkeleton />
    </div>
  );
  if (variant === "individual-content") return (
    <div data-ak-v2-component="skeleton-layout" data-variant={variant}><IndividualContentSkeleton /></div>
  );
  if (variant === "record-actions") return (
    <div className="akv:contents" data-ak-v2-component="skeleton-layout" data-variant={variant}>
      <Skeleton className="akv:h-10 akv:w-44 akv:rounded-ak-control" variant="custom" />
      <Skeleton className="akv:h-10 akv:w-48 akv:rounded-ak-control" variant="custom" />
      <Skeleton className="akv:h-10 akv:w-52 akv:rounded-ak-control" variant="custom" />
      <Skeleton className="akv:h-10 akv:w-48 akv:rounded-ak-control" variant="custom" />
      <Skeleton className="akv:h-10 akv:w-10 akv:rounded-ak-control" variant="custom" />
    </div>
  );
  if (variant === "questionnaire") return (
    <div className="akv:grid akv:gap-5" data-ak-v2-component="skeleton-layout" data-variant={variant}>
      <HeaderSkeleton />
      <TabsSkeleton count={2} />
      <div className="akv:flex akv:flex-wrap akv:gap-4">
        {Array.from({ length: 4 }, (_, index) => <Skeleton className="akv:h-3 akv:w-24" key={index} variant="custom" />)}
      </div>
      <div className="akv:flex akv:items-center akv:justify-between akv:gap-5 akv:rounded-ak-surface akv:bg-ak-surface akv:p-5 akv:shadow-ak-surface">
        <div className="akv:grid akv:flex-1 akv:gap-3"><Skeleton className="akv:w-2/3" variant="text" /><Skeleton className="akv:w-1/3" variant="text" /></div>
        <Skeleton variant="button" />
      </div>
      <MetricsSkeleton />
      <div className="akv:grid akv:gap-3">
        {Array.from({ length: 3 }, (_, index) => (
          <div className="akv:grid akv:gap-4 akv:rounded-ak-surface akv:bg-ak-surface akv:p-5 akv:shadow-ak-surface" key={index}>
            <Skeleton className="akv:h-3 akv:w-32" variant="custom" />
            <Skeleton className="akv:w-3/5" variant="title" />
            <div className="akv:grid akv:gap-2"><Skeleton variant="text" /><Skeleton className="akv:w-5/6" variant="text" /><Skeleton className="akv:w-2/3" variant="text" /></div>
          </div>
        ))}
      </div>
    </div>
  );
  // Liste des groupes (cartes) et campagnes : l'ancien dessin de liste.
  if (variant === "group-list" || variant === "campaign") return (
    <div className="akv:grid akv:gap-6" data-ak-v2-component="skeleton-layout" data-variant={variant}><HeaderSkeleton /><MetricsSkeleton count={3} /><RegistrySkeleton cards={variant === "group-list"} /></div>
  );
  return null;
};

FeatureSkeleton.propTypes = { variant: PropTypes.string.isRequired };

export function SkeletonLayout({ variant = "content" }) {
  useThemeA("SkeletonLayout");

  if (variant.startsWith("analysis-")) return <AnalysisSkeleton kind={variant.slice(9)} />;

  if (["analysis", "campaign", "enclosure", "group", "individual", "individual-content", "questionnaire", "record-actions", "sector", "tour", "veterinary", "group-list", "group-info", "enclosure-dashboard", "sector-dashboard"].includes(variant)) {
    return <FeatureSkeleton variant={variant} />;
  }

  if (variant === "table") {
    return (
      <div className="akv:grid akv:gap-3" data-ak-v2-component="skeleton-layout" data-variant={variant}>
        <Skeleton className="akv:h-12" variant="block" />
        {Array.from({ length: 5 }, (_, index) => (
          <div className="akv:flex akv:items-center akv:gap-4 akv:rounded-ak-control akv:bg-ak-surface akv:px-4 akv:py-3" key={index}>
            <Skeleton variant="avatar" />
            <Skeleton className="akv:flex-1" variant="text" />
            <Skeleton className="akv:w-24" variant="text" />
          </div>
        ))}
      </div>
    );
  }

  if (variant === "detail") {
    return (
      <div className="akv:grid akv:gap-6" data-ak-v2-component="skeleton-layout" data-variant={variant}>
        <div className="akv:grid akv:gap-3">
          <Skeleton variant="title" />
          <Skeleton className="akv:w-1/2" variant="text" />
        </div>
        <Skeleton className="akv:h-56" variant="block" />
        <div className="akv:grid akv:grid-cols-2 akv:gap-4 akv:max-[42rem]:grid-cols-1">
          <CardSkeleton />
          <CardSkeleton />
        </div>
      </div>
    );
  }

  return (
    <div className="akv:grid akv:gap-6" data-ak-v2-component="skeleton-layout" data-variant={variant}>
      <HeaderSkeleton />
      <div className="akv:grid akv:grid-cols-3 akv:gap-4 akv:max-[68rem]:grid-cols-2 akv:max-[42rem]:grid-cols-1">
        <CardSkeleton />
        <CardSkeleton />
        <CardSkeleton />
      </div>
    </div>
  );
}

SkeletonLayout.propTypes = {
  variant: PropTypes.oneOf(["analysis-scan", "analysis-focal", "analysis-questionnaire", "analysis-enrichment", "analysis", "campaign", "content", "detail", "enclosure", "group", "group-info", "group-list", "individual", "individual-content", "enclosure-dashboard", "questionnaire", "record-actions", "sector", "sector-dashboard", "table", "tour", "veterinary"]),
};
